interface DataConnection extends EventTarget { readonly saveData?: boolean }
interface HeroVideoOptions { enabled?: boolean; reducedMotion?: boolean }

/** One native video in the existing Core surface; the SSR poster is always the fallback. */
export function initHeroVideo({ enabled = true, reducedMotion = false }: HeroVideoOptions = {}) {
  const stage = document.querySelector<HTMLElement>("[data-hero-video]");
  const poster = stage?.querySelector<HTMLImageElement>("img");
  const control = stage?.parentElement?.querySelector<HTMLButtonElement>("[data-hero-video-toggle]");
  if (!stage || !poster) return { destroy() {} };
  const surface = stage;
  const connection = (navigator as Navigator & { connection?: DataConnection }).connection;
  const mobile = window.matchMedia("(max-width: 800px)");
  let video: HTMLVideoElement | null = null;
  let mediaEvents: AbortController | null = null;
  let videoFrame: number | null = null;
  let paintFrame: number | null = null;
  let startTimer: number | null = null;
  let generation = 0;
  let disposed = false, visible = false, ready = false, failed = false, playPending = false;
  let pausedByUser = surface.dataset.videoUserPaused === "true";

  const setState = (state: string): void => { surface.dataset.videoState = state; };
  const syncControl = (): void => {
    if (!control) return;
    control.setAttribute("aria-pressed", String(pausedByUser));
    control.setAttribute("aria-label", (pausedByUser ? control.dataset.playLabel : control.dataset.pauseLabel) ?? "Pause animation");
  };
  const releaseVideo = (): void => {
    generation++;
    playPending = false;
    mediaEvents?.abort(); mediaEvents = null;
    if (videoFrame !== null) video?.cancelVideoFrameCallback(videoFrame);
    if (paintFrame !== null) window.cancelAnimationFrame(paintFrame);
    videoFrame = null; paintFrame = null;
    surface.classList.remove("is-video-ready");
    if (video) {
      video.pause(); video.removeAttribute("src"); video.load(); video.remove(); video = null;
    }
    if (control) control.hidden = true;
  };
  const mayPlay = (): boolean => !disposed && enabled && !reducedMotion && !connection?.saveData
    && ready && visible && !document.hidden && !pausedByUser && !failed;

  const fallback = (state: string): void => { failed = true; releaseVideo(); setState(state); };
  const showFrame = (sequence: number): void => {
    videoFrame = null; paintFrame = null;
    if (sequence !== generation || !video || !mayPlay()) return;
    surface.classList.add("is-video-ready");
    if (control) control.hidden = false;
    syncControl(); setState("playing");
  };
  const sync = (): void => {
    if (disposed) return;
    if (!enabled || reducedMotion || connection?.saveData) {
      releaseVideo(); setState(connection?.saveData ? "save-data" : "static"); return;
    }
    if (failed) return;
    if (!ready || !visible || document.hidden || pausedByUser) {
      video?.pause();
      if (control && pausedByUser) control.hidden = false;
      setState(pausedByUser ? "user-paused" : !ready ? "poster" : "paused"); return;
    }
    if (!video) {
      const source = mobile.matches ? surface.dataset.mobileSrc : surface.dataset.desktopSrc;
      if (!source) { fallback("missing-source"); return; }
      const sequence = ++generation;
      const media = document.createElement("video");
      video = media;
      mediaEvents = new AbortController();
      media.muted = true; media.defaultMuted = true; media.playsInline = true; media.loop = true;
      media.preload = "none";
      media.setAttribute("aria-hidden", "true");
      media.setAttribute("disablepictureinpicture", "");
      media.addEventListener("error", () => {
        if (sequence === generation) fallback("media-error");
      }, { signal: mediaEvents.signal });
      media.addEventListener("playing", () => {
        if (sequence !== generation) return;
        if (!mayPlay()) { media.pause(); return; }
        if (videoFrame !== null) media.cancelVideoFrameCallback(videoFrame);
        if (paintFrame !== null) window.cancelAnimationFrame(paintFrame);
        // Hide the poster only after the decoder has supplied a real frame.
        if ("requestVideoFrameCallback" in media) {
          videoFrame = media.requestVideoFrameCallback(() => showFrame(sequence));
        } else {
          paintFrame = window.requestAnimationFrame(() => showFrame(sequence));
        }
      }, { signal: mediaEvents.signal });
      media.src = source;
      surface.append(media);
    }
    if (playPending || !video.paused) return;
    const sequence = generation;
    playPending = true;
    void video.play().catch((error: DOMException) => {
      if (sequence !== generation || disposed || error.name === "AbortError") return;
      fallback("autoplay-blocked");
    }).finally(() => {
      if (sequence !== generation) return;
      playPending = false;
      // A rapid leave/return can abort play() before the observer resumes it.
      if (mayPlay() && video?.paused) sync();
    });
  };

  const observer = new IntersectionObserver(([entry]) => {
    visible = Boolean(entry?.isIntersecting); sync();
  }, { threshold: .01 });
  observer.observe(surface);
  const onSourceChange = (): void => { releaseVideo(); sync(); };
  const onConnectionChange = (): void => sync();
  const onToggle = (): void => {
    pausedByUser = !pausedByUser;
    surface.dataset.videoUserPaused = String(pausedByUser);
    syncControl(); sync();
  };
  mobile.addEventListener("change", onSourceChange);
  connection?.addEventListener("change", onConnectionChange);
  document.addEventListener("visibilitychange", sync);
  control?.addEventListener("click", onToggle);
  syncControl(); sync();

  // The high-priority SSR poster wins the initial paint. MP4 starts after load,
  // decoding the poster and the existing 1.5 s Hero entrance, never before it.
  const afterLoad = (): void => {
    void poster.decode().catch(() => {}).then(() => {
      if (disposed) return;
      startTimer = window.setTimeout(() => { startTimer = null; ready = true; sync(); }, 1600);
    });
  };
  if (document.readyState === "complete") afterLoad();
  else window.addEventListener("load", afterLoad, { once: true });

  return {
    destroy() {
      disposed = true; observer.disconnect(); releaseVideo();
      if (startTimer !== null) window.clearTimeout(startTimer);
      window.removeEventListener("load", afterLoad);
      mobile.removeEventListener("change", onSourceChange);
      connection?.removeEventListener("change", onConnectionChange);
      document.removeEventListener("visibilitychange", sync);
      control?.removeEventListener("click", onToggle);
    },
  };
}
